<template>
  <div>
    <EasyDataTable
      ref="dataTable"
      v-model:server-options="serverOptions"
      table-class-name="vue3-easy-data-table table-shadow no-overflow"
      :headers="headers"
      :items="transactions"
      :server-items-length="serverItemsLength"
      :loading="loading"
      :body-row-class-name="rowClassName"
      hide-footer
      no-hover
      @update:server-options="updateServerOptions"
    >
      <template #item="{ column, item }">
        <span v-if="column === 'createdAt'">
          <RowDate :date="item.createdAt" />
        </span>
        <span v-else-if="column === 'product'">{{ productLabel(item.product) }}</span>
        <span v-else-if="column === 'sourceType'">{{ sourceTypeLabel(item.sourceType) }}</span>
        <span v-else-if="column === 'sourceReference'">{{ item.sourceReference }}</span>
        <span v-else-if="column === 'qtyDelta'" :class="item.qtyDelta > 0 ? 'text-success' : 'text-danger'">
          {{ item.qtyDelta }}
        </span>
        <span v-else>{{ item[column] }}</span>
      </template>
      <template #no-data>-</template>
    </EasyDataTable>

    <TablePagination
      v-model:server-options-ref="serverOptions"
      v-model:data-table-ref="dataTable"
      :server-items-length="serverItemsLength"
    />
  </div>
</template>

<script lang="ts">
import type { InventoryTransaction } from '@/types/Models/InventoryTransaction'
import { defineComponent, computed, ref, toRef } from 'vue'
import { useInventoryServerPagination } from '@/composables/useInventoryServerPagination'
import { useInventoryStore } from '@/store/inventory'
import EasyDataTable, { type Header } from 'vue3-easy-data-table'
import RowDate from '@/views/Components/Elements/Table/RowDate.vue'
import TablePagination from '@/views/Components/Elements/Table/TablePagination.vue'

export default defineComponent({
  name: 'InventoryTransactionsPage',
  components: { EasyDataTable, RowDate, TablePagination },
  props: {
    companyId: {
      type: String,
      required: true,
    },
  },
  setup(props) {
    const inventoryStore = useInventoryStore()
    const transactions = computed(() => inventoryStore.transactionsList)
    const loading = computed(() => inventoryStore.transactionsLoading)
    const serverItemsLength = computed(() => inventoryStore.transactionsCount)
    const dataTable = ref()
    const companyId = toRef(props, 'companyId')

    const productLabel = (product: { sku: string; name: string }) => `${product.sku} · ${product.name}`

    const sourceTypeLabels: Partial<Record<InventoryTransaction['sourceType'], string>> = {
      outbound_shipment_line: 'Shipment',
      inbound_receipt_line: 'Inbound delivery',
    }
    const sourceTypeLabel = (sourceType: InventoryTransaction['sourceType']) =>
      sourceTypeLabels[sourceType] ?? sourceType

    const rowClassName = (item: InventoryTransaction) => (item.qtyDelta > 0 ? 'row-replenish' : 'row-deduction')

    const headers: Header[] = [
      { text: 'Created', value: 'createdAt' },
      { text: 'Product', value: 'product' },
      { text: 'Qty', value: 'qtyDelta' },
      { text: 'Source type', value: 'sourceType' },
      { text: 'Source', value: 'sourceReference' },
    ]

    const { serverOptions, updateServerOptions } = useInventoryServerPagination(companyId, async (options) => {
      await inventoryStore.fetchTransactions(companyId.value, { page: options.page, rowsPerPage: options.rowsPerPage })
    })

    return {
      dataTable,
      transactions,
      loading,
      serverItemsLength,
      headers,
      serverOptions,
      updateServerOptions,
      productLabel,
      sourceTypeLabel,
      rowClassName,
    }
  },
})
</script>

<style scoped>
:deep(.row-replenish td) {
  background-color: #f0fff4;
}
:deep(.row-deduction td) {
  background-color: #fff5f5;
}
</style>
